/**
 * MCP Tool descriptions for Deckspace
 *
 * This file is the SSOT for tool names, descriptions, and hints.
 * Schema shape is owned by schemas.ts (Zod) — the MCP SDK converts
 * Zod schemas to JSON Schema automatically. No duplicate inputSchema here.
 *
 * 6 tools total:
 * - Structural: workspace (CRUD), page (CRUD)
 * - Data: data (insert|update|delete_rows|delete_dataset)
 * - Surface: deckspace_ui (create|rebuild|surgical update)
 * - State: list, detail
 *
 * The `deckspace_ui` description is auto-generated at module load from each
 * `VisualizationDefinition.description` in `./visualizations/`. The catalog
 * is the SSOT — adding a new visualization automatically extends the tool
 * description without touching this file.
 */
import { ALL_VISUALIZATIONS } from './visualizations/index.js';
// ============================================================================
// Structural Tools
// ============================================================================
/**
 * deckspace_workspace - Workspace CRUD
 */
export const workspaceTool = {
    name: 'deckspace_workspace',
    description: `Create, rename, update, or delete a workspace. Every session starts here — create a workspace to get an ID, then use that ID in all other tool calls.

Workspace IDs use the format \`wsp\` + 16 base62 chars (e.g., \`wspABC123def456gh\`).

**action: "create"** — Create a new workspace. Returns the workspace ID.

Required: action, name
Optional: metadata ({ purpose, context, tags })

Example:
\`\`\`
deckspace_workspace({ action: "create", name: "Q1 Revenue Analysis" })
→ { id: "wspABC123def456gh", name: "Q1 Revenue Analysis" }
\`\`\`

After creating a workspace, create at least one page with deckspace_page before adding content.

**action: "rename"** — Rename an existing workspace.

Required: action, id, name

Example:
\`\`\`
deckspace_workspace({ action: "rename", id: "a1b2c3d4-...", name: "Revenue Dashboard" })
\`\`\`

**action: "update_metadata"** — Update workspace metadata (partial merge).

Required: action, id
Optional: metadata ({ purpose, context, tags })

Example:
\`\`\`
deckspace_workspace({
  action: "update_metadata",
  id: "a1b2c3d4-...",
  metadata: { purpose: "Track quarterly revenue", tags: ["finance", "q1"] }
})
\`\`\`

**action: "delete"** — Delete a workspace and everything inside it (pages, datasets, visualizations, canvas items).

Required: action, id

Example:
\`\`\`
deckspace_workspace({ action: "delete", id: "a1b2c3d4-..." })
\`\`\``,
};
/**
 * deckspace_page - Page CRUD within a workspace
 */
export const pageTool = {
    name: 'deckspace_page',
    description: `Create, rename, reorder, or delete pages within a workspace.

Page IDs use the format \`pag\` + 16 base62 chars (e.g., \`pagABC123def456gh\`).
Workspace IDs use the format \`wsp\` + 16 base62 chars.

Every workspace needs at least one page before content can be added.

**action: "create"** — Create a new page in a workspace.

Required: action, workspaceId, name
Optional: sortOrder (defaults to last position)

Example:
\`\`\`
deckspace_page({
  action: "create",
  workspaceId: "a1b2c3d4-...",
  name: "Overview"
})
→ { id: "p1e2f3...", name: "Overview", sortOrder: 0 }
\`\`\`

**action: "rename"** — Rename an existing page.

Required: action, id, name

Example:
\`\`\`
deckspace_page({ action: "rename", id: "p1e2f3...", name: "Executive Summary" })
\`\`\`

**action: "delete"** — Delete a page and all its items. Does not delete datasets.

Required: action, id

**action: "reorder"** — Set page display order. Unlisted pages sort after listed ones.

Required: action, workspaceId, order (array of page IDs)

Example:
\`\`\`
deckspace_page({
  action: "reorder",
  workspaceId: "a1b2c3d4-...",
  order: ["p1e2f3...", "p4a5b6..."]
})
\`\`\``,
};
// ============================================================================
// Data Tools
// ============================================================================
/**
 * deckspace_data - Create datasets, insert/update/delete data
 */
export const dataTool = {
    name: 'deckspace_data',
    description: `Create datasets with typed schemas, then insert, update, or delete data. Dispatches on the \`action\` field. Use the workspace ID from deckspace_workspace as the workspaceId (format: \`wsp\` + 16 base62 chars).

**action: "create"** — Create a new dataset with a typed schema. Every column must have a type. This must be called before inserting data.

Required fields: action, workspaceId, dataset, schema
Optional: rows (validated against schema and inserted atomically)

Column types:
- text — any string value
- number — numeric values. Optional unit (kg, km, mph, etc.)
- date — ISO 8601 dates (2025-01-01, 2025-03, 2025). Sorted chronologically.
- boolean — true or false
- url — must start with http:// or https://
- percent — numeric values stored as 0–1 ratios. The UI multiplies by 100 at display, so write \`0.89\` to render \`89.00%\`.
- currency — numeric values. Requires currency code (USD, EUR, GBP)

Every column may also include an optional \`label\` field to override the auto-generated header. Use \`label\` to fix acronyms (\`mrr\` → \`MRR\`, \`dau\` → \`DAU\`) or unit suffixes (\`p50_ms\` → \`p50 (ms)\`) that the default snake-case-to-title transform mangles. Identifier and addressing key stay as the storage \`name\`.

Example — create with schema and data:
\`\`\`
deckspace_data({
  action: "create",
  workspaceId: "<workspace-uuid>",
  dataset: "metrics",
  schema: {
    date: { type: "date" },
    mrr: { type: "currency", currency: "USD", label: "MRR" },
    growth_rate: { type: "percent" },
    region: { type: "text" }
  },
  rows: [
    { date: "2025-01-01", mrr: 50000, growth_rate: 0.12, region: "NA" },
    { date: "2025-02-01", mrr: 65000, growth_rate: 0.30, region: "NA" }
  ]
})
\`\`\`

Example — create empty dataset:
\`\`\`
deckspace_data({
  action: "create",
  workspaceId: "<workspace-uuid>",
  dataset: "files",
  schema: {
    path: { type: "text" },
    lines: { type: "number" },
    link: { type: "url" }
  }
})
\`\`\`

**action: "insert"** — Insert rows into an existing dataset. Schema is already defined. Every value is validated against the column type.

Required fields: action, workspaceId, dataset, rows

Example:
\`\`\`
deckspace_data({
  action: "insert",
  workspaceId: "<workspace-uuid>",
  dataset: "sales",
  rows: [
    { date: "2025-03-01", revenue: 70000, region: "EU", active: true }
  ]
})
\`\`\`

**action: "update"** — Update existing rows matching a WHERE clause. Uses JSONB merge — only the fields in \`set\` are changed. Values validated against column types.

Required fields: action, workspaceId, dataset, where, set

Example:
\`\`\`
deckspace_data({
  action: "update",
  workspaceId: "<workspace-uuid>",
  dataset: "sales",
  where: [{ column: "region", op: "=", value: "NA" }],
  set: { active: false }
})
\`\`\`

**action: "delete_rows"** — Delete rows matching a WHERE clause. Cannot delete all rows without a filter (safety).

Required fields: action, workspaceId, dataset, where

Example:
\`\`\`
deckspace_data({
  action: "delete_rows",
  workspaceId: "<workspace-uuid>",
  dataset: "sales",
  where: [{ column: "active", op: "=", value: false }]
})
\`\`\`

**action: "delete_dataset"** — Drop an entire dataset and all its rows. Also removes any surfaces that reference it.

Required fields: action, workspaceId, dataset

Example:
\`\`\`
deckspace_data({
  action: "delete_dataset",
  workspaceId: "<workspace-uuid>",
  dataset: "sales"
})
\`\`\`

**action: "schema"** — Add columns to an existing dataset. Cannot change existing column types.

Required fields: action, workspaceId, dataset, schema

Example:
\`\`\`
deckspace_data({
  action: "schema",
  workspaceId: "<workspace-uuid>",
  dataset: "sales",
  schema: {
    growth: { type: "percent" }   // store 0.14 to render 14.00%
  }
})
\`\`\``,
};
/**
 * deckspace_list - Browse workspace hierarchy (summaries only)
 */
export const listTool = {
    name: 'deckspace_list',
    description: `Browse workspace hierarchy. Returns summaries only — use deckspace_detail for full content.

ID formats: workspaceId is \`wsp\` + 16 base62 chars; pageId is \`pag\` + 16 base62 chars.

Drill-down levels:
- No args → list all workspaces (id, name)
- workspaceId → dataset catalog (names, columns, row counts) + pages
- pageId → surface summary for the page (component tree overview, type counts)

Always start with no args to discover workspaces, then drill down.

Examples:
\`\`\`
deckspace_list()
deckspace_list({ workspaceId: "<uuid>" })
deckspace_list({ pageId: "<uuid>" })
\`\`\``,
    hints: { defer_loading: true },
};
/**
 * Order of category sections in the auto-generated surface tool description.
 * Categories without registered visualizations are silently skipped.
 */
const CATEGORY_ORDER = [
    'layout',
    'content',
    'data',
    'interactive',
    'domain',
];
const CATEGORY_LABEL = {
    layout: 'Layout',
    content: 'Content',
    data: 'Data-backed',
    interactive: 'Interactive',
    domain: 'Domain',
};
/**
 * Build the deckspace_ui description by stitching the static header,
 * per-category bullets from the catalog, and a static examples footer.
 *
 * Each catalog entry contributes one bullet of the form
 * `- **<type>**: <description>`. Categories are listed in CATEGORY_ORDER;
 * within each category, types are sorted alphabetically so that adding a
 * new viz produces a stable, predictable diff.
 */
function generateSurfaceDescription() {
    const header = `The sole creation tool for all visual content — charts, data views, diagrams, and rich surfaces. Each page has one surface. Two modes:

Page IDs use the format \`pag\` + 16 base62 chars (e.g., \`pagABC123def456gh\`).

**Create / full rebuild** — provide \`pageId\` + \`components\`:
- Upserts the page's single surface (creates on first call, replaces on subsequent calls)
- components: flat array of component nodes (adjacency list)
- Each node: { id, type, children?, props? }
- Must include exactly one node with id "root"
- Root node's children define the top-level layout

**Surgical update** — provide \`pageId\` + \`updates\`:
- Patch individual nodes without replacing the full tree
- Each update: { nodeId, props?, children?, remove? }
- Use remove: true to delete a node from the tree

**Sizing model:** Surfaces present as flowing documents — the page scrolls naturally as content grows. Most blocks (Text, Metric, Callout, Image, charts) size to their content. Data-backed scrollable types (DataTable, Kanban, List) render in fixed-height windows that scroll internally when their content exceeds the cell — compose them inline with other blocks the way you'd embed a database in a Notion page. Override a window's height per-instance via \`style.height\` on the node.

**Component types:**`;
    // Group by category and sort each group alphabetically by type.
    const byCategory = new Map();
    for (const def of ALL_VISUALIZATIONS) {
        const arr = byCategory.get(def.category) ?? [];
        arr.push(def);
        byCategory.set(def.category, arr);
    }
    for (const arr of byCategory.values()) {
        arr.sort((a, b) => a.type.localeCompare(b.type));
    }
    const sections = [];
    for (const category of CATEGORY_ORDER) {
        const items = byCategory.get(category);
        if (!items || items.length === 0)
            continue;
        const body = items.map((def) => `- **${def.type}**: ${def.description}`).join('\n');
        sections.push(`${CATEGORY_LABEL[category]}:\n${body}`);
    }
    const examples = `**Example — chart with KPI header:**
\`\`\`
deckspace_ui({
  pageId: "<page-uuid>",
  components: [
    { id: "root", type: "Column", children: ["header", "chart"] },
    { id: "header", type: "Row", children: ["title", "badge"], props: { align: "center" } },
    { id: "title", type: "Text", props: { text: "Revenue", variant: "h3" } },
    { id: "badge", type: "Badge", props: { text: "+12.5%", color: "green" } },
    { id: "chart", type: "LineChart", props: { sourceDataset: "monthly_revenue", mapping: { x: "month", y: "revenue" } } }
  ]
})
\`\`\`

**Example — two charts side by side:**
\`\`\`
deckspace_ui({
  pageId: "<page-uuid>",
  components: [
    { id: "root", type: "Row", props: { tracks: ["2fr", "1fr"] }, children: ["left", "right"] },
    { id: "left", type: "LineChart", props: { sourceDataset: "revenue", mapping: { x: "month", y: "amount" } } },
    { id: "right", type: "BarChart", props: { sourceDataset: "expenses", mapping: { x: "category", y: "amount" } } }
  ]
})
\`\`\`

**Example — surgical update (change a metric value):**
\`\`\`
deckspace_ui({
  pageId: "<page-uuid>",
  updates: [
    { nodeId: "revenue_metric", props: { value: "$1.2M", trend: "+8%" } }
  ]
})
\`\`\`

Max 50 nodes, max 10 nesting depth.`;
    return `${header}\n\n${sections.join('\n\n')}\n\n${examples}`;
}
/**
 * deckspace_ui - Create or update a surface
 *
 * Description is auto-generated from `VisualizationDefinition.description`
 * in `./visualizations/`. See `generateSurfaceDescription` above.
 */
export const surfaceTool = {
    name: 'deckspace_ui',
    description: generateSurfaceDescription(),
};
// ============================================================================
// State Tools
// ============================================================================
/**
 * deckspace_detail - Full payload for a single entity
 */
export const detailTool = {
    name: 'deckspace_detail',
    description: `Get full detail for a single surface or dataset. Use deckspace_list first to find IDs.

ID formats: workspaceId is \`wsp\` + 16 base62 chars; itemId (surface) is \`srf\` + 16 base62 chars.

Modes:
- itemId → full surface config (component tree)
- workspaceId + dataset → column profiles, sample rows

Optional dataset params:
- where: [{ column, op, value }] — filter rows (ops: =, !=, >, <, >=, <=, LIKE, IN)
- limit: number (default 5, max 50) — cap returned rows

Examples:
\`\`\`
deckspace_detail({ itemId: "<uuid>" })
deckspace_detail({ workspaceId: "<uuid>", dataset: "sales" })
deckspace_detail({ workspaceId: "<uuid>", dataset: "sales", where: [{ column: "region", op: "=", value: "NA" }], limit: 10 })
\`\`\``,
    hints: { defer_loading: true },
};
// ============================================================================
// Tool Exports
// ============================================================================
/**
 * All available tools (6 total)
 *
 * - Structural: workspace (CRUD), page (CRUD)
 * - Data: data (insert|update|delete_rows|delete_dataset)
 * - Surface: deckspace_ui (create|rebuild|surgical update)
 * - State: list, detail
 */
export const allTools = [
    // Structural tools (2)
    workspaceTool,
    pageTool,
    // Data tools (1)
    dataTool,
    // Surface (1)
    surfaceTool,
    // State tools (2)
    listTool,
    detailTool,
];
export const remoteTools = allTools;
//# sourceMappingURL=tools.js.map